import PropTypes from 'prop-types';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Layout, Menu, Drawer } from 'antd';

import useMedia from '@/hooks/useMedia';

// import { navigationMenus } from './navigation'
import '@/assets/style/sideNav.css';
import '@/assets/style/modern-sidebar.css';
import {
  AppstoreOutlined,
  BankOutlined,
  BookOutlined,
  ContainerOutlined,
  DashboardOutlined,
  DollarOutlined,
  FileTextOutlined,
  GoldOutlined,
  MoneyCollectOutlined,
  PieChartOutlined,
  PrinterOutlined,
  ProfileOutlined,
  SettingOutlined,
  ShopOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { decryptData } from '@/components/reusable/crypto/Cipher';
import Cookies from 'js-cookie';
import { userData } from '@/common/custom-date-selector-list/helpers';

const { Sider } = Layout;

// Logo served from the public folder (referenced by absolute path).
const logo = '/icon.png';

const iconMapping = {
  UserOutlined: <UserOutlined />,
  DashBoardOutlined: <DashboardOutlined />,
  DashboardOutlined: <DashboardOutlined />,
  BookOutlined: <BookOutlined />,
  MoneyCollectOutlined: <MoneyCollectOutlined />,
  PieChartOutlined: <PieChartOutlined />,
  GoldOutlined: <GoldOutlined />,
  PrinterOutlined: <PrinterOutlined />,
  BankOutlined: <BankOutlined />,
  ShopOutlined: <ShopOutlined />,
  TeamOutlined: <TeamOutlined />,
  DollarOutlined: <DollarOutlined />,
  WalletOutlined: <WalletOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  ProfileOutlined: <ProfileOutlined />,
  ContainerOutlined: <ContainerOutlined />,
  TagsOutlined: <TagsOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  SettingOutlined: <SettingOutlined />,
  DefaultIcon: <SettingOutlined />,
};

// Pool of icons used when the backend does not send an icon for a menu item.
const fallbackIcons = [
  <AppstoreOutlined key="appstore" />,
  <BankOutlined key="bank" />,
  <BookOutlined key="book" />,
  <ContainerOutlined key="container" />,
  <DollarOutlined key="dollar" />,
  <FileTextOutlined key="file" />,
  <GoldOutlined key="gold" />,
  <PieChartOutlined key="pie" />,
  <ProfileOutlined key="profile" />,
  <ShopOutlined key="shop" />,
  <TagsOutlined key="tags" />,
  <TeamOutlined key="team" />,
  <WalletOutlined key="wallet" />,
];

// Stable string hash so a given menu item always gets the same "random" icon
// (avoids the icon flickering/changing on every re-render or navigation).
const hashString = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // force 32-bit int
  }
  return Math.abs(hash);
};

/**
 * Resolves the icon for a menu item.
 * - If the backend provides a mappable icon, that icon is used.
 * - Otherwise a stable icon is picked from the fallback pool.
 *
 * @param {Object} item - The raw menu item.
 * @returns {JSX.Element} The resolved icon element.
 */
const resolveMenuIcon = (item) => {
  if (item?.icon && iconMapping[item.icon]) {
    return iconMapping[item.icon];
  }
  const seed = String(item?.id ?? item?.name ?? '');
  return fallbackIcons[hashString(seed) % fallbackIcons.length];
};

const AdminSideBar = ({ collapsed, colorBgContainer, setCollapsed }) => {
  const [openKeys, setOpenKeys] = useState([]);
  const [navigationMenus, setNavigationMenus] = useState([]);
  const navigate = useNavigate();
  const isMobile = useMedia('mobile');
  const currentUrl = window.location.pathname;

  const siderStyle = {
    overflow: 'auto',
    height: '100vh',
    position: 'sticky',
    left: 0,
    top: 0,
    bottom: 0,
  };

  const toNavigate = (item) => {
    navigate(item.key);
  };

  const onClose = () => {
    setCollapsed(false);
  };

  // testAminData;

  const data = localStorage?.getItem('testAminData')
    ? decryptData(localStorage?.getItem('testAminData'))
    : [];

  // const data = Cookies.get('__adminMenu__')
  //   ? decryptData(Cookies.get('__adminMenu__'))
  //   : [];

  /**s
   * Transforms the raw menu data to the required format.
   *
   * @param {Array} menuData - The raw menu data.
   * @returns {Array} The transformed menu data.
   */
  const transformMenuData = (menuData) => {
    const transformItem = (item) => ({
      // key: item.id,
      key: item?.subMenus?.length
        ? item.id
        : item?.listUrl || item?.createUrl || `#`,
      // Use the backend icon when provided/mappable, otherwise assign a
      // stable icon from the fallback pool.
      icon: resolveMenuIcon(item),
      id: item.id,
      label: item.name,
      children: item.subMenus ? item.subMenus.map(transformItem) : null,
    });

    // Insert a divider line after each top-level menu item.
    const itemsWithDividers = [];
    menuData.forEach((item, index) => {
      itemsWithDividers.push(transformItem(item));
      itemsWithDividers.push({ type: 'divider', key: `divider-${index}` });
    });

    return itemsWithDividers;
  };

  useEffect(() => {
    const fetchAndSetMenuData = () => {
      const menuData = transformMenuData(data);
      setNavigationMenus(menuData);
    };
    fetchAndSetMenuData();
  }, []);

  /**
   * Handles the change of open submenu keys.
   *
   * @param {Array} keys - The open keys.
   */
  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);

    if (!latestOpenKey) {
      setOpenKeys([]);
      return;
    }
    setOpenKeys([latestOpenKey]);
  };

  return (
    <>
      {isMobile ? (
        <Drawer open={collapsed} placement="left" onClose={onClose}>
          <Menu
            mode="inline"
            disabledOverflow={false}
            style={{
              backgroundColor: colorBgContainer,
            }}
            items={navigationMenus}
            selectedKeys={[currentUrl]}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={(item) => {
              toNavigate(item);
              setCollapsed(false);
            }}
          />
        </Drawer>
      ) : (
        <Sider
          className="modern-sidebar admin-modern-sidebar"
          trigger={null}
          width={240}
          collapsible
          collapsed={collapsed}
          style={{
            ...siderStyle,
            overflowX: 'hidden',
          }}
        >
          <div className="sidebar-logo-container">
            <img
              src={logo}
              alt="Logo"
              onClick={() => navigate('/admin/dashboard')}
              style={{ cursor: 'pointer' }}
              className={
                collapsed
                  ? 'sidebar-logo sidebar-logo-collapsed'
                  : 'sidebar-logo'
              }
            />
          </div>

          <Menu
            mode="inline"
            disabledOverflow={false}
            items={navigationMenus}
            selectedKeys={[currentUrl]}
            overflowedIndicator
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={toNavigate}
          />
        </Sider>
      )}
    </>
  );
};

AdminSideBar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  colorBgContainer: PropTypes.string,
  setCollapsed: PropTypes.func,
};

export default AdminSideBar;
